import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteCard, changeCardStatus, getSpecialCards, getAllSpecialCards, getFilteredSpecialCards, convertSpecialCardToStandardCard } from "../../redux/cardSlice";
import { getCardTypes } from "../../redux/cardTypeSlice";
import { message, Card, Spin } from "antd";
import SpecialCardsDrawer from "./SpecialCardsDrawer";
import SpecialCardsFilter from "./Filter/SpecialCardsFilter";
import Header from "../../global/Header/Header";
import CustomTableComponents from "../CommonComponents/CustomTableComponents";
import { style } from "../../Styles";
import { MinusOutlined } from "@ant-design/icons";
import ActionButtonWithChangeStatus from "../CommonComponents/ActionButtonWithChangeStatus";
import { useTranslation } from "react-i18next";
import { formatDateAndTime } from "../../Utils/Imports/date-utils";
import debounce from "lodash.debounce";
import LostCardLogsModal from "./LostCardLogsModal";

const SpecialCards = () => {
  const { t } = useTranslation();
  const columns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => {
        // Calculate the actual row number based on current page and page size
        const currentPage = cards?.currentPage || 1;
        const pageSize = cards?.pageSize || 10;
        return <span>{(currentPage - 1) * pageSize + index + 1}</span>;
      },
      width: 60,
      show: true,
    },
    {
      title: t("specialCardScreen.table.cardId"),
      key: "cardId",
      render: (record) => <span>{record.cardNumber || <MinusOutlined />}</span>,
      show: true,
    },
    {
      title: t("specialCardScreen.table.cardType"),
      key: "cardType",
      render: (record) => (
        <span>{record.populatedCardType.name || <MinusOutlined />}</span>
      ),
      show: true,
    },    {
      title: t("specialCardScreen.table.name"),
      key: "name",
      render: (record) => (
        <span>{record.cardHolder?.name || <MinusOutlined />}</span>
      ),
      show: true,
    },
    {
      title: t("specialCardScreen.table.surname"),
      key: "surname",
      render: (record) => (
        <span>{record.cardHolder?.surname || <MinusOutlined />}</span>
      ),
      show: true,
    },
    {
      title: t("specialCardScreen.table.idNumber"),
      key: "idNumber",
      render: (record) => (
        <span>{record.cardHolder?.identityCardNumber || <MinusOutlined />}</span>
      ),
      show: true,
    },
    {
      title: t("specialCardScreen.table.validFrom"),
      key: "validFrom",
      render: (record) => {
        const date = record.cardValidFrom ? formatDateAndTime(record.cardValidFrom) : <MinusOutlined />;
        return <span>{date}</span>;
      },
      sorter: (a, b) =>
        (a.cardValidFrom || "").localeCompare(b.cardValidFrom || ""),
      show: true,
    },
    {
      title: t("specialCardScreen.table.validTo"),
      key: "validTo",
      render: (record) => {
        const date = record.cardValidTo ? formatDateAndTime(record.cardValidTo) : <MinusOutlined />;
        return <span>{date}</span>;
      },
      sorter: (a, b) =>
        (a.cardValidTo || "").localeCompare(b.cardValidTo || ""),
      show: true,
    },
    {
      title: t("specialCardScreen.table.bundleValidFrom"),
      key: "bundleValidFrom",
      render: (record) => {
        const date = record.bundleValidFrom ? formatDateAndTime(record.bundleValidFrom) : <MinusOutlined />;
        return <span>{date}</span>;
      },
      sorter: (a, b) =>
        (a.bundleValidFrom || "").localeCompare(b.bundleValidFrom || ""),
      show: true,
    },
    {
      title: t("specialCardScreen.table.bundleValidTo"),
      key: "bundleValidTo",
      render: (record) => {
        const date = record.bundleValidTo ? formatDateAndTime(record.bundleValidTo): <MinusOutlined />;
        return <span>{date}</span>;
      },
      sorter: (a, b) =>
        (a.bundleValidTo || "").localeCompare(b.bundleValidTo || ""),
      show: true,
    },
    {
      title: t("specialCardScreen.table.replaced"),
      key: "active",
      render: (record) => {
        if (!record?.cardReplaced) {
          return <MinusOutlined />;
        }
        return (
          <span style={style.dataContainer}>
            {record?.cardReplaced.length > 0 ? (
              <div onClick={() => showLostCardLogsModal(record)} style={{...style.statusActif, cursor: "pointer"}}>
                {t("statuses.replaced")}
              </div>
            ) : (
              <MinusOutlined />
            )}
          </span>
        );
      },
      sorter: (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1),
      show: true,
    },
    {
      title: t("validatorScreen.table.status"),
      key: "active",
      render: (record) => {
        return (
          <span style={style.dataContainer}>
            {record.active ? (
              <div style={style.statusActif}>{t("statuses.active")}</div>
            ) : (
              <div style={style.statusInactif}>{t("statuses.inactive")}</div>
            )}
          </span>
        );
      },
      sorter: (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1),
      show: true,
    },
    {
      title: t("specialCardScreen.table.actions"),
      key: "actions",
      render: (text, record) => (
        <ActionButtonWithChangeStatus
          record={record}
          onDelete={() => handleDeleteSpecialCard(record.id)}
          onRowClick={(record) => handleSelectedRow(record)}
          onChangeStatus={() => handleChangeStatus(record)}
          onConvertToStandard={() => handleConvertToStandard(record.id)}
        />
      ),
      show: true,
    },
  ];
  const [isMakingApiCall, setMakingApiCall] = useState(true);
  const [isSpecialCardDrawerVisible, setSpecialCardDrawerVisible] =
    useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const [card, setCard] = useState(null);
  const [filter, setFilter] = useState("");
  const [filterValues, setFilterValues] = useState({
    cardTypeName: null,
    cardValidFrom: null,
    cardValidTo: null,
    bundleValidityFrom: null,
    bundleValidityTo: null,
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([
    {
      label: t("cardScreen.table.index"),
      key: "index",
    },
    {
    label: t("cardScreen.table.cardNumber"),
    key: "cardNumber",
  },
  {
    label: t("specialCardScreen.table.name"),
    key: "name",
  },
  {
    label: t("specialCardScreen.table.surname"),
    key: "surname",
  }, 
  {
    label: t("specialCardScreen.table.validFrom"),
    key: "cardValidFrom",
  },
  {
    label: t("specialCardScreen.table.validTo"),
    key: "cardValidTo",
  },
  {
    label: t("specialCardScreen.table.bundleValidFrom"),
    key: "bundleValidFrom",
  },
  {
    label: t("specialCardScreen.table.bundleValidTo"),
    key: "bundleValidTo",
  },]);
  const [csvData, setCsvData] = useState([]);  const dispatch = useDispatch();
  const [selectedLostCardLogs, setSelectedLostCardLogs] = useState([]);
  const [selectedCardForLostCardLogs, setSelectedCardForLostCardLogs] = useState(null);
  const [isCardLostLogsModalOpen, setIsCardLostLogsModalOpen] = useState(false);
  const { cards } = useSelector((state) => state.cards);
    const fetchCards = useCallback((page = 1, pageSize = 10) => {
    if (isFiltered) {
      // Use filter endpoint
      const filterData = {
        ...filterValues,
        page: page - 1,
        size: pageSize,
        search: filter,
      };
      dispatch(getFilteredSpecialCards(filterData))
        .unwrap()
        .then(() => {
          setMakingApiCall(false);
        })
        .catch(() => {
          setMakingApiCall(false);
        });
    } else {
      // Use regular endpoint
      dispatch(getSpecialCards({page, pageSize, search: filter}))
        .unwrap()
        .then(() => {
          setMakingApiCall(false);
        })
        .catch(() => {
          setMakingApiCall(false);
        });
    }
  }, [dispatch, filter, isFiltered, filterValues]);
  useEffect(() => {
    // Load card types on component mount
    dispatch(getCardTypes());
  }, [dispatch]);
  useEffect(() => {
    //fetchCards();
    const delayedSearch = debounce(() => {
      fetchCards();
    }, 500); // Wait 500ms after the last keystroke

    delayedSearch();
    return delayedSearch.cancel;
  }, [fetchCards]);

  const reload = () => {
    // setReloadFlag((prev) => !prev);
    fetchCards();
  };

  const showLostCardLogsModal = (record) => {
    setSelectedLostCardLogs(record?.cardReplaced)
    setSelectedCardForLostCardLogs(record?.cardNumber);
    setIsCardLostLogsModalOpen(true)
  }

  const closeCardLostLogsModal = () => {
    setSelectedCardForLostCardLogs(null);
    setSelectedLostCardLogs([])
    setIsCardLostLogsModalOpen(false)
  }

  const handleChangeStatus = (record) => {
    dispatch(
      changeCardStatus({
        id: record.id,
        status: !record.active,
      })
    )
      .unwrap()
      .then(() => {
        message.success(
          t("specialCardScreen.messages.specialCardStatusChangedSuccessfully")
        );
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  /* const filteredSpecialCards = cards?.data?.filter((item) => {
    return (
      item.cardHolder != null &&
      item.populatedCardType != null &&
      (
        item.cardNumber
          .toString()
          .toLowerCase()
          ?.includes(filter.toLowerCase()) ||
        item.populatedCardType.name
          .toLowerCase()
          .includes(filter.toLowerCase()) ||
        item.cardHolder.name.toLowerCase().includes(filter.toLowerCase()) ||
        item.cardHolder.surname.toLowerCase().includes(filter.toLowerCase()) ||
        item.cardHolder.identityCardNumber.toLowerCase().includes(filter.toLowerCase())
      )
    );
  }); */

  const openDrawer = () => {
    setSpecialCardDrawerVisible(true);
  };

  const closeDrawer = () => {
    setSpecialCardDrawerVisible(false);
    setCard(null);
  };

  const handleSelectedRow = (record) => {
    setCard(record);
    setSpecialCardDrawerVisible(true);
  };
  const handleDeleteSpecialCard = (id) => {
    dispatch(deleteCard(id))
      .unwrap()
      .then(() => {
        message.success(t("specialCardScreen.messages.specialCardDeletedSuccessfully"));
        fetchCards();
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  const handleConvertToStandard = (id) => {
    dispatch(convertSpecialCardToStandardCard(id))
      .unwrap()
      .then(() => {
        message.success(t("specialCardScreen.messages.cardConvertedToStandardSuccessfully"));
        fetchCards();
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  const openFilterDrawer = () => {
    setIsFilterDrawerVisible(true);
  };

  const handleApplyFilter = (filterData) => {
    setFilterValues(filterData);
    setIsFiltered(true);
    setIsFilterDrawerVisible(false);
    fetchCards();
  };

  const handleResetFilter = () => {
    setFilterValues({
      cardTypeName: null,
      cardValidFrom: null,
      cardValidTo: null,
      bundleValidityFrom: null,
      bundleValidityTo: null,
    });
    setIsFiltered(false);
    fetchCards();
  };

  const fetchAllCards = async () => {
    setMakingApiCall(true);
    try {
      let data;
      if (isFiltered) {
        // Use filter endpoint with filter values
        const filterData = {
          ...filterValues,
          page: 0,
          size: 10000, // Fetch a large number to get all filtered results
          search: filter,
        };
        const response = await dispatch(getFilteredSpecialCards(filterData)).unwrap();
        data = response.content || response.data || response; // Handle both paginated and non-paginated responses
      } else {
        // Use regular endpoint
        const response = await dispatch(getAllSpecialCards({ search: filter })).unwrap();
        data = response.content || response.data || response; // Handle both paginated and non-paginated responses
      }
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error("Data is not an array:", data);
        setMakingApiCall(false);
        return [];
      }
      
      setMakingApiCall(false);
      console.log("allCard", data);
      return data.map((item) => {
        return {...item, 
          name: item.cardHolder != null ? item.cardHolder.name : "-",
          surname: item.cardHolder != null ? item.cardHolder.surname : "-",
          cardValidFrom: item.bundleValidFrom ? formatDateAndTime(item.bundleValidFrom) : "-",
          cardValidTo: item.cardValidTo ? formatDateAndTime(item.cardValidTo) : "-",
          bundleValidFrom: item.bundleValidFrom ? formatDateAndTime(item.bundleValidFrom) : "-",
          bundleValidTo: item.bundleValidTo ? formatDateAndTime(item.bundleValidTo) : "-",
          active: item.active ? t("statuses.active") :t("statuses.inactive"),
        }
      }); // Return the data to the caller
    } catch (error) {
      console.error("Error fetching cards", error);
      setMakingApiCall(false);
      return []; // Return empty array on error
    }
  };
  const handleGenerateCsv = async (event, done) => {
    try {
      const data = await fetchAllCards();
      const headers = [
        {
          label: "#",
          key: "index",
        },
        {
          label: t("cardScreen.table.cardNumber"),
          key: "cardNumber",
        },
        {
          label: t("specialCardScreen.table.name"),
          key: "name",
        },
        {
          label: t("specialCardScreen.table.surname"),
          key: "surname",
        }, 
        {
          label: t("specialCardScreen.table.validFrom"),
          key: "cardValidFrom",
        },
        {
          label: t("specialCardScreen.table.validTo"),
          key: "cardValidTo",
        },
        {
          label: t("specialCardScreen.table.bundleValidFrom"),
          key: "bundleValidFrom",
        },
        {
          label: t("specialCardScreen.table.bundleValidTo"),
          key: "bundleValidTo",
        },
        {
          label: t("cardScreen.table.status"),
          key: "active",
        },
      ];
      
      setCsvData(data);
      setCsvHeaders(headers);
      
      // Call done with the data and headers for immediate export
      if (done) {
        done(data, headers);
      }
    } catch (error) {
      console.error("Error generating CSV:", error);
      message.error(t("messages.errorGeneratingCsv") || "Error generating CSV");
      if (done) {
        done([], []);
      }
    }
  };

  return (
    <>
      {isMakingApiCall ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>          <Header
            addButtonText={t("specialCardScreen.header.addSpecialCard")}
            onAddButtonClick={openDrawer}
            onSearch={(e) => {
              setFilter(e.target.value);
            }}
            searchPlaceholder={t("specialCardScreen.header.search")}
            addButtonTextColor={"#ffffff"}
            onReload={fetchCards}
            showAddButton={true}
            BreadcrumbItems={[
              {
                title: t("specialCardScreen.header.title"),
              },
            ]}
            showBreadcrumb={true}
            showExportCSV={true}
            csvHeaders={csvHeaders}
            csvData={csvData}
            generateCSVfn={handleGenerateCsv}
            csvDataMiddleFileName={"SpecialCards"}
            fetchDataPdf={fetchAllCards}            showFilterButton={true}
            onClickFilterButton={openFilterDrawer}
            isFiltered={isFiltered}
          />
          <Card style={{ margin: 16, height: "85vh" }}>
            <CustomTableComponents
              isMakingApiCall={isMakingApiCall}
              columns={columns.filter((column) => column.show)}
              dataArray={cards?.data}
              handleDoubleClickRow={(record) => handleSelectedRow(record)}
              handleSelectedRow={() => {}}
              paginationMeta={cards?.totalCards}
              onPageChange={(page, pageSize) => fetchCards(page, pageSize)}
            />
          </Card>          {isSpecialCardDrawerVisible && (
            <SpecialCardsDrawer
              specialCardId={card?.id}
              specialCardHolderId={card?.cardHolder?.id}
              onClose={closeDrawer}
              reload={reload}
            />
          )}
          {isFilterDrawerVisible && (
            <SpecialCardsFilter
              isDrawerOpen={isFilterDrawerVisible}
              setIsDrawerOpen={setIsFilterDrawerVisible}
              loading={isMakingApiCall}
              onSave={handleApplyFilter}
              onResetForm={handleResetFilter}
              filterValues={filterValues}
              setFilterValues={setFilterValues}
            />
          )}

          {isCardLostLogsModalOpen && (
                    <LostCardLogsModal
                      isModalOpen={isCardLostLogsModalOpen}
                      handleCancel={closeCardLostLogsModal}
                      lostCardLogs={selectedLostCardLogs}
                      cardNumber={selectedCardForLostCardLogs}
                    />
                  )}
        </>
      )}
    </>
  );
};

export default SpecialCards;
